/* Schedule tab: the queue grouped by day. One filter row (campaign, sort, a
   segmented status filter with counts, then selection, time zone, Refresh and
   Regenerate on the right) sits directly above the table. Rows show the
   visible window, the message, its attachments (thumbnails; click for a
   preview) and the status in purple/neutral tones. Rows — or a whole day —
   can be ticked and taken back together: Mass deletes them, Priority cancels
   them (removing already-sent ones from fans' chats). */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { CalendarDateTime } from '@internationalized/date';
import {
  SCHEDULE_TIME_ZONE_OPTIONS,
  formatScheduleDay,
  formatScheduleTime,
  getScheduleTimeZone,
  nowInScheduleTimeZone,
  parseScheduleTime,
  saveScheduleTimeZone,
  scheduleDayKey,
  scheduleTimeToApiValue,
  scheduleTimeToEpoch,
} from '../../../utils/scheduleTime';
import { api } from '../../../api/client';
import { Btn, Icon, Loading, Spin } from '../../ChatAssistant/compact/kit';
import { MediaLightbox } from '../../ChatAssistant/compact/MediaLightbox';
import { MediaPicker } from '../../ChatAssistant/compact/MediaPicker';
import { ThumbStrip } from '../../ChatAssistant/compact/ThumbStrip';
import { itemThumb, useResolveIds } from '../../ChatAssistant/compact/resolveIds';
import { fmtDuration, isVideoItem } from '../../ChatAssistant/SalesLadder/MediaBadge';
import { useSalesCatalogue, type CatalogueItem, type SalesCatalogue } from '../../ChatAssistant/SalesLadder/useSalesCatalogue';
import { VaultThumb } from '../../ChatAssistant/Sales/VaultThumb';
import { font as f } from '../../ChatAssistant/compact/style';
import { C } from '../../ChatAssistant/compact/tokens';
import { massApi } from './massApi';
import type { MassData } from './MassMessagesPage';
import { canEdit, canRemove, contentIds, matchesFilter, rowStatus, type Camp, type Kind, type Row, type RowStatus, type StatusFilter } from './model';
import { Check, Dialog, DialogHead, IconBtn, Menu, Panel, PanelRow, SectionHead, STRIPES } from './parts';

const COLS = '20px 124px minmax(0,1fr) 176px 96px 60px';
const ROW_H = 50;

interface View { r: Row; send: CalendarDateTime; del: CalendarDateTime; at: number; day: string; camp: Camp | undefined }

const STATUS: Record<RowStatus, { ic: string; c: string; bg: string; bd: string }> = {
  pending: { ic: 'clock', c: C.purplePale, bg: C.tint16, bd: 'color-mix(in srgb, var(--om-accent) 30%, transparent)' },
  sent: { ic: 'check', c: C.text, bg: C.segActive, bd: 'var(--om-border-strong)' },
  unsent: { ic: 'eye-off', c: C.sub, bg: 'transparent', bd: C.borderHi },
  failed: { ic: 'alert-circle', c: C.purplePale, bg: 'transparent', bd: C.purple },
  removing: { ic: 'loader', c: C.sub, bg: 'transparent', bd: C.borderHi },
};

export function SchedulePage({ kind, accountId, data, camp, setCamp, onOpenSetup }: {
  kind: Kind; accountId: string; data: MassData; camp: number | 'all'; setCamp: (c: number | 'all') => void; onOpenSetup?: () => void;
}) {
  const { t } = useTranslation('massCompact');
  const a = useMemo(() => massApi(kind, accountId), [kind, accountId]);
  const [tz, setTz] = useState(getScheduleTimeZone);
  const [st, setSt] = useState<StatusFilter>('pending');
  const [desc, setDesc] = useState(false);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);
  const [preview, setPreview] = useState<Row | null>(null);
  const [regen, setRegen] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const pri = kind === 'priority';

  const campOf = useMemo(() => new Map(data.camps.map((c) => [c.id!, c])), [data.camps]);
  const fc = camp === 'all' ? undefined : campOf.get(camp);
  // A filter on a campaign that's gone falls back to all.
  useEffect(() => { if (camp !== 'all' && !data.loading && !campOf.has(camp)) setCamp('all'); }, [camp, campOf, data.loading, setCamp]);
  useEffect(() => { setSel(new Set()); }, [camp, st]);

  // Priority cancels finish in the background — keep the queue fresh while any are in flight.
  const inFlight = data.rows.some((r) => rowStatus(r) === 'removing');
  const { reloadRows } = data;
  useEffect(() => {
    if (!inFlight) return;
    const id = setInterval(() => { void reloadRows(true); }, 5000);
    return () => clearInterval(id);
  }, [inFlight, reloadRows]);

  const views = useMemo(() => {
    const out: View[] = [];
    for (const r of data.rows) {
      try {
        const send = parseScheduleTime(r.scheduled_send_at, tz);
        out.push({ r, send, del: parseScheduleTime(r.scheduled_delete_at, tz), at: scheduleTimeToEpoch(send, tz), day: scheduleDayKey(send), camp: campOf.get(r.campaign_id) });
      } catch { /* a row with an unreadable timestamp can't be placed on the timeline */ }
    }
    return out;
  }, [data.rows, tz, campOf]);

  const byCamp = views.filter((v) => camp === 'all' || v.r.campaign_id === camp);
  const shown = byCamp.filter((v) => matchesFilter(v.r, st)).sort((x, y) => (desc ? y.at - x.at : x.at - y.at));
  const days: { key: string; rows: View[] }[] = [];
  shown.forEach((v) => { const last = days[days.length - 1]; if (last && last.key === v.day) last.rows.push(v); else days.push({ key: v.day, rows: [v] }); });
  const queued = byCamp.filter((v) => matchesFilter(v.r, 'pending'));
  const qDays = new Set(queued.map((v) => v.day)).size;

  // Thumbnails: resolve the attachments of the rows on screen.
  const cat = useSalesCatalogue(accountId);
  const ids = useMemo(() => [...new Set(shown.flatMap((v) => contentIds(v.r)))], [shown]);
  const thumbsLoading = useResolveIds(accountId, cat, ids);

  const today = scheduleDayKey(nowInScheduleTimeZone(tz));
  const dayLabel = (v: View) => {
    const base = formatScheduleDay(v.send, tz);
    const rel = v.day === today ? 'today' : v.day === scheduleDayKey(nowInScheduleTimeZone(tz).add({ days: 1 })) ? 'tomorrow' : v.day === scheduleDayKey(nowInScheduleTimeZone(tz).subtract({ days: 1 })) ? 'yesterday' : null;
    return rel ? `${t(`sch.${rel}`)} · ${base}` : base;
  };

  const toggle = (rowIds: number[]) => setSel((s) => {
    const n = new Set(s);
    const on = rowIds.every((i) => n.has(i));
    rowIds.forEach((i) => (on ? n.delete(i) : n.add(i)));
    return n;
  });

  const applyRemoved = (id: number, updated: Row | null) => data.setRows((rs) => (updated ? rs.map((x) => (x.id === id ? { ...x, ...updated } : x)) : rs.filter((x) => x.id !== id)));
  const removeOne = async (r: Row) => {
    const sentAlready = pri && r.status !== 'pending';
    if (!confirm(pri ? (sentAlready ? t('sch.confirmRemoveChats') : t('sch.confirmCancel')) : t('sch.confirmDelete'))) return;
    try { applyRemoved(r.id, await a.removeEntry(r.id)); setSel((s) => { const n = new Set(s); n.delete(r.id); return n; }); }
    catch (e) { console.error('Failed to remove schedule entry:', e); alert(pri ? t('sch.cancelFailed') : t('sch.deleteFailed')); }
  };
  const removeSelected = async () => {
    if (!sel.size || !confirm(pri ? t('sch.confirmBulkCancel', { count: sel.size }) : t('sch.confirmBulkDelete', { count: sel.size }))) return;
    setBusy(true);
    let fail = 0;
    for (const id of sel) {
      try { applyRemoved(id, await a.removeEntry(id)); } catch (e) { console.error(`Failed to remove schedule entry ${id}:`, e); fail++; }
    }
    setBusy(false);
    setSel(new Set());
    if (fail) alert(t('sch.bulkPartial', { ok: sel.size - fail, fail }));
    void data.reloadRows(true);
  };

  const campOpts = [
    { id: 'all', label: t('sch.allCampaigns'), sub: String(views.filter((v) => matchesFilter(v.r, 'pending')).length || ''), checked: camp === 'all' },
    ...data.camps.map((c) => ({ id: String(c.id), label: c.name, sub: c.active ? String(views.filter((v) => v.r.campaign_id === c.id && matchesFilter(v.r, 'pending')).length || '') : t('sch.off'), checked: camp === c.id })),
  ];
  const statusOpts: { id: StatusFilter; ic: string }[] = [
    { id: 'all', ic: 'list' }, { id: 'pending', ic: 'clock' }, { id: 'sent', ic: 'check' }, { id: 'unsent', ic: 'eye-off' }, { id: 'failed', ic: 'alert-circle' },
  ];
  const tzLabel = SCHEDULE_TIME_ZONE_OPTIONS.find((o) => o.id === tz)?.label ?? tz;

  const empty = (() => {
    if (!data.camps.length) return { title: t('sch.noCampsTitle'), text: t('sch.noCampsText') };
    if (fc && !fc.active) return { title: t('sch.offTitle', { name: fc.name }), text: t('sch.offText') };
    if (fc && fc.source !== 'auto' && fc.dist.length && fc.dist.every((d) => !data.lists.some((l) => String(l.id) === d.id))) return { title: t('sch.emptyTitle'), text: t('sch.listsGone') };
    return { title: t('sch.emptyTitle'), text: t('sch.emptyText') };
  })();

  if (data.loading) return <div className="cpt-root" style={{ padding: '0 20px' }}><Loading /></div>;

  return (
    <div className="cpt-root">
      <div style={{ padding: '6px 4px 56px', width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <SectionHead
          title={t('sch.title')}
          sub={queued.length
            ? t('sch.summary', { messages: t('sch.messages', { count: queued.length }), days: t('sch.days', { count: qDays }) }) + (fc ? ` · ${fc.name}` : '') + '.'
            : fc ? t('sch.nothingFor', { name: fc.name }) : t('sch.nothing')}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Menu
            look={fc ? 'tinted' : 'field'}
            icon="target"
            iconColor={C.purpleLt}
            label={fc ? fc.name : t('sch.allCampaigns')}
            options={campOpts}
            onPick={(id) => setCamp(id === 'all' ? 'all' : Number(id))}
          />
          <Menu
            icon="arrow-down"
            label={desc ? t('sch.latest') : t('sch.earliest')}
            width={180}
            options={[{ id: 'asc', label: t('sch.earliest'), checked: !desc }, { id: 'desc', label: t('sch.latest'), checked: desc }]}
            onPick={(id) => setDesc(id === 'desc')}
          />
          <span style={{ width: 1, height: 18, background: C.borderHi }} />
          <div style={{ display: 'flex', gap: 2, padding: 2, background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, flexWrap: 'wrap' }}>
            {statusOpts.map((o) => {
              const on = st === o.id;
              return (
                <button key={o.id} type="button" onClick={() => setSt(o.id)} title={o.id === 'all' ? undefined : t(`status.${o.id}Tip`)} style={{ height: 26, padding: '0 9px', border: 0, borderRadius: 6, background: on ? C.segActive : 'transparent', color: on ? C.text : C.sub, font: f(500, 12), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                  <Icon name={o.ic} size={12} />{t(`status.${o.id}`)}
                  <span style={{ font: f(600, 10.5), color: C.sub, fontVariantNumeric: 'tabular-nums' }}>{byCamp.filter((v) => matchesFilter(v.r, o.id)).length}</span>
                </button>
              );
            })}
          </div>
          <span style={{ flex: 1 }} />
          {sel.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ font: f(500, 12), color: C.purplePale, whiteSpace: 'nowrap' }}>{t('sch.selected', { count: sel.size })}</span>
              <Btn icon={busy ? undefined : 'trash-2'} disabled={busy} onClick={() => { void removeSelected(); }} style={{ background: C.input }}>{busy && <Spin size={12} />}{pri ? t('sch.cancelSel') : t('sch.deleteSel')}</Btn>
              <button type="button" onClick={() => setSel(new Set())} className="cpt-hover-text" style={{ height: 28, padding: '0 8px', border: 0, borderRadius: 7, background: 'transparent', color: C.sub, font: f(500, 12), cursor: 'pointer' }}>{t('sch.clear')}</button>
            </div>
          )}
          <Menu
            icon="globe"
            label={tzLabel}
            align="right"
            width={260}
            options={SCHEDULE_TIME_ZONE_OPTIONS.map((o) => ({ id: o.id, label: o.label, checked: o.id === tz }))}
            onPick={(id) => { saveScheduleTimeZone(id); setTz(id); }}
          />
          <Btn icon="refresh-cw" onClick={() => { void data.reloadRows(); }} style={{ background: C.input, borderColor: C.border }}>{t('sch.refresh')}</Btn>
          <Btn kind="primary" icon="zap" disabled={!data.camps.length} title={t('sch.regenTip')} onClick={() => setRegen(true)}>{t('sch.regenerate')}</Btn>
        </div>

        {notice && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', borderRadius: 8, border: `1px solid ${notice.ok ? C.tintLine : C.purple}`, background: 'color-mix(in srgb, var(--om-accent) 10%, transparent)', font: f(500, 12, 1.45), color: notice.ok ? C.text : C.purplePale }}>
            <Icon name={notice.ok ? 'check' : 'alert-circle'} size={13} style={{ marginTop: 2, flex: 'none' }} />
            <span style={{ flex: 1 }}>{notice.text}</span>
            <IconBtn icon="x" title={t('close')} size={22} iconSize={12} onClick={() => setNotice(null)} />
          </div>
        )}

        <div className="cpt-thin-scroll" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflowX: 'auto' }}>
          <div style={{ minWidth: 860 }}>
            <div style={{ display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', columnGap: 16, height: 32, padding: '0 14px', font: f(600, 10), letterSpacing: '.08em', textTransform: 'uppercase', color: C.muted }}>
              <span /><span>{t('sch.colTime')}</span><span>{t('sch.colMessage')}</span><span>{t('sch.colAttached')}</span><span>{t('sch.colStatus')}</span><span />
            </div>
            {data.rowsLoading && !data.rows.length ? (
              <div style={{ borderTop: `1px solid ${C.divider}`, padding: '0 14px' }}><Loading /></div>
            ) : days.map((d) => {
              const pickable = d.rows.filter((v) => canRemove(kind, v.r)).map((v) => v.r.id);
              const n = pickable.filter((i) => sel.has(i)).length;
              return (
                <div key={d.key}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, height: 36, padding: '0 14px', borderTop: `1px solid ${C.divider}`, background: 'var(--om-panel)' }}>
                    <Check on={n > 0 && n === pickable.length} partial={n > 0 && n < pickable.length} disabled={!pickable.length} onClick={() => toggle(pickable)} title={t('sch.pickDay')} />
                    <span style={{ width: 6, height: 6, borderRadius: 99, background: d.key === today ? C.purple : 'var(--om-border-strong)' }} />
                    <span style={{ font: f(600, 12.5), color: C.text, whiteSpace: 'nowrap' }}>{dayLabel(d.rows[0]!)}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ font: f(500, 11.5), color: C.sub, whiteSpace: 'nowrap' }}>{t('sch.messages', { count: d.rows.length })}</span>
                  </div>
                  {d.rows.map((v) => (
                    <ScheduleRow
                      key={v.r.id}
                      kind={kind}
                      v={v}
                      tz={tz}
                      cat={cat}
                      accountId={accountId}
                      thumbsLoading={thumbsLoading}
                      picked={sel.has(v.r.id)}
                      onPick={() => toggle([v.r.id])}
                      onPreview={() => setPreview(v.r)}
                      onEdit={() => setEditing(v.r)}
                      onRemove={() => { void removeOne(v.r); }}
                    />
                  ))}
                </div>
              );
            })}
            {!days.length && !(data.rowsLoading && !data.rows.length) && (
              <div style={{ padding: '32px 14px', borderTop: `1px solid ${C.divider}`, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
                <span style={{ font: f(500, 13), color: C.text }}>{empty.title}</span>
                <span style={{ font: f(400, 12), color: C.sub, maxWidth: 420 }}>{empty.text}</span>
                {onOpenSetup && (
                  <button type="button" onClick={onOpenSetup} className="cpt-hover" style={{ marginTop: 4, height: 28, padding: '0 10px', border: `1px solid ${C.borderHi}`, borderRadius: 7, background: C.input, color: C.purplePale, font: f(500, 12), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="settings" size={13} />{t('sch.openSetup')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {preview && <PreviewDialog accountId={accountId} cat={cat} row={preview} tz={tz} camp={campOf.get(preview.campaign_id)} dayLabel={(v) => dayLabel(v)} onClose={() => setPreview(null)} />}
      {editing && (
        <EditDialog
          kind={kind}
          accountId={accountId}
          cat={cat}
          row={editing}
          tz={tz}
          onClose={() => setEditing(null)}
          onSaved={(r) => { data.setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, ...r } : x))); setEditing(null); }}
        />
      )}
      {regen && (
        <RegenDialog
          kind={kind}
          accountId={accountId}
          camps={data.camps}
          initial={fc?.id ?? data.camps[0]?.id ?? null}
          onClose={() => setRegen(false)}
          onDone={(ok, text) => { setRegen(false); setNotice({ ok, text }); if (ok) void data.reloadRows(); }}
        />
      )}
    </div>
  );
}

function ScheduleRow({ kind, v, tz, cat, accountId, thumbsLoading, picked, onPick, onPreview, onEdit, onRemove }: {
  kind: Kind; v: View; tz: string; cat: SalesCatalogue; accountId: string; thumbsLoading: boolean; picked: boolean;
  onPick: () => void; onPreview: () => void; onEdit: () => void; onRemove: () => void;
}) {
  const { t } = useTranslation('massCompact');
  const { r } = v;
  const keep = !!v.camp?.keep;
  let s = rowStatus(r);
  if (keep && s === 'unsent') s = 'sent';
  const done = s === 'unsent';
  const m = STATUS[s];
  const media = contentIds(r);
  const vis = Math.round((scheduleTimeToEpoch(v.del, tz) - v.at) / 60000);
  const pickable = canRemove(kind, r);
  const removeTitle = kind === 'priority' ? (r.status === 'pending' ? t('sch.cancelOne') : t('sch.removeChats')) : t('sch.deleteOne');
  return (
    <div className="cpt-hover-row" style={{ display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', columnGap: 16, minHeight: ROW_H, padding: '6px 14px', boxSizing: 'border-box', borderTop: `1px solid ${C.divider}`, background: picked ? 'color-mix(in srgb, var(--om-accent) 8%, transparent)' : 'transparent' }}>
      <Check on={picked} disabled={!pickable} onClick={onPick} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, font: f(600, 13), color: done ? C.sub : C.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {formatScheduleTime(v.send, tz)}<Icon name="arrow-right" size={11} color={C.muted} />
          <span style={{ color: C.sub, fontWeight: 500 }}>{keep ? t('sch.kept') : formatScheduleTime(v.del, tz)}</span>
        </span>
        <span style={{ font: f(400, 11), color: C.muted, whiteSpace: 'nowrap' }}>{keep ? t('sch.neverDeleted') : vis > 0 ? t('sch.visibleMin', { count: vis }) : t('sch.visibleWindow')}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span title={r.message_text} style={{ font: f(400, 12.5), color: done ? C.sub : C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.message_text || <span style={{ color: C.muted }}>{t('sch.noText')}</span>}</span>
        <span style={{ font: f(400, 11), color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.camp?.name ?? t('sch.deletedCampaign')}</span>
      </div>
      <div>
        {media.length ? (
          <button type="button" onClick={onPreview} title={t('sch.viewAttachments')} className="cpt-hover" style={{ display: 'flex', alignItems: 'center', padding: 2, margin: -2, border: 0, borderRadius: 7, background: 'transparent', cursor: 'pointer' }}>
            <ThumbStrip accountId={accountId} ids={media} byId={cat.byId} max={3} size={34} loading={thumbsLoading} />
          </button>
        ) : (
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, font: f(400, 11.5), color: C.muted, whiteSpace: 'nowrap' }}><Icon name="type" size={12} />{t('sch.textOnly')}</span>
        )}
      </div>
      <span title={t(`status.${s}Tip`)} style={{ justifySelf: 'start', height: 22, padding: '0 8px', display: 'flex', alignItems: 'center', gap: 5, boxSizing: 'border-box', border: `1px solid ${m.bd}`, borderRadius: 99, background: m.bg, color: m.c, font: f(500, 11.5), whiteSpace: 'nowrap' }}>
        <Icon name={m.ic} size={11} />{t(`status.${s}`)}
      </span>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        {canEdit(kind, r) && <IconBtn icon="edit-2" title={t('sch.edit')} size={26} iconSize={12} color={C.muted} onClick={onEdit} />}
        {pickable && <IconBtn icon="trash-2" title={removeTitle} size={26} iconSize={12} color={C.muted} onClick={onRemove} />}
      </div>
    </div>
  );
}

/** A scheduled message with all its attachments; a tile opens it full screen. */
function PreviewDialog({ accountId, cat, row, tz, camp, dayLabel, onClose }: {
  accountId: string; cat: SalesCatalogue; row: Row; tz: string; camp: Camp | undefined; dayLabel: (v: View) => string; onClose: () => void;
}) {
  const { t } = useTranslation('massCompact');
  const [full, setFull] = useState<number | null>(null);
  const ids = contentIds(row);
  const send = parseScheduleTime(row.scheduled_send_at, tz);
  const del = parseScheduleTime(row.scheduled_delete_at, tz);
  const items = ids.map((id) => cat.byId.get(id)).filter((x): x is CatalogueItem => !!x);
  const head = `${dayLabel({ r: row, send, del, at: 0, day: scheduleDayKey(send), camp })} · ${formatScheduleTime(send, tz)}${camp?.keep ? '' : ` → ${formatScheduleTime(del, tz)}`}`;
  return (
    <>
      <Dialog width={620} onClose={onClose} closeOnBackdrop locked={full !== null}>
        <DialogHead title={head} sub={`${camp?.name ?? t('sch.deletedCampaign')} · ${t('sch.attachments', { count: ids.length })}`} onClose={onClose} closeTitle={t('close')} />
        <div className="cpt-thin-scroll" style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
          {row.message_text && <div style={{ alignSelf: 'flex-start', maxWidth: '100%', padding: '8px 12px', background: C.tint16, border: '1px solid color-mix(in srgb, var(--om-accent) 30%, transparent)', borderRadius: 10, font: f(400, 13, 1.45), color: C.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row.message_text}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 8 }}>
            {ids.map((id) => {
              const it = cat.byId.get(id);
              const vid = it ? isVideoItem(it.type) : false;
              const k = it ? items.indexOf(it) : -1;
              return (
                <div key={id} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <button type="button" disabled={!it} onClick={() => setFull(k)} style={{ aspectRatio: '3/4', padding: 0, borderRadius: 8, overflow: 'hidden', background: STRIPES, border: `1px solid ${C.borderHi}`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', cursor: it ? 'zoom-in' : 'default' }}>
                    {it ? <VaultThumb accountId={accountId} url={itemThumb(it)} className="w-full h-full" /> : <Spin size={14} />}
                    {vid && it?.duration ? (
                      <span style={{ position: 'absolute', left: 6, bottom: 6, padding: '1px 5px', borderRadius: 4, background: 'color-mix(in srgb, var(--om-page) 82%, transparent)', color: C.text, font: f(600, 10.5), display: 'flex', alignItems: 'center', gap: 3 }}><Icon name="play" size={9} />{fmtDuration(it.duration)}</span>
                    ) : null}
                  </button>
                  <span style={{ font: f(400, 11), color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{id}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Dialog>
      {full !== null && items.length > 0 && <MediaLightbox accountId={accountId} items={items} start={Math.max(0, full)} onClose={() => setFull(null)} />}
    </>
  );
}

/** Change one queued message: when it goes out, when it's deleted, its text and attachment. */
function EditDialog({ kind, accountId, cat, row, tz, onClose, onSaved }: {
  kind: Kind; accountId: string; cat: SalesCatalogue; row: Row; tz: string; onClose: () => void; onSaved: (r: Row) => void;
}) {
  const { t } = useTranslation('massCompact');
  const a = useMemo(() => massApi(kind, accountId), [kind, accountId]);
  const [send, setSend] = useState(() => parseScheduleTime(row.scheduled_send_at, tz));
  const [del, setDel] = useState(() => parseScheduleTime(row.scheduled_delete_at, tz));
  const [text, setText] = useState(row.message_text);
  const [media, setMedia] = useState<string[]>(contentIds(row));
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const loading = useResolveIds(accountId, cat, media);
  const bad = !text.trim() ? t('edit.needText') : del.compare(send) <= 0 ? t('edit.deleteAfter') : null;

  const save = async () => {
    if (bad || saving) return;
    setSaving(true);
    try {
      const had = contentIds(row).join(',');
      const now = media.join(',');
      const body: Parameters<typeof a.updateEntry>[1] = { message_text: text.trim(), scheduled_send_at: scheduleTimeToApiValue(send, tz), scheduled_delete_at: scheduleTimeToApiValue(del, tz) };
      // null clears the attachment; leaving it out keeps it.
      if (now !== had) (body as Record<string, unknown>).content_id = now || null;
      onSaved(await a.updateEntry(row.id, body));
    } catch (e) {
      console.error('Failed to update schedule entry:', e);
      alert(t('edit.failed'));
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog width={520} onClose={onClose} locked={picking || saving}>
        <DialogHead icon="calendar" title={t('edit.title')} onClose={onClose} closeTitle={t('close')} />
        <div className="cpt-thin-scroll" style={{ padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
          <Panel>
            <PanelRow label={t('edit.send')}><DateTime value={send} onChange={setSend} /></PanelRow>
            <PanelRow label={t('edit.delete')}><DateTime value={del} onChange={setDel} /></PanelRow>
          </Panel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ font: f(600, 13), color: C.text }}>{t('edit.message')}</span>
            <textarea value={text} onChange={(e) => setText(e.target.value)} style={{ minHeight: 80, resize: 'vertical', padding: '8px 10px', boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, font: f(400, 12.5, 1.5), outline: 'none' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ font: f(600, 13), color: C.text, marginRight: 4 }}>{t('edit.attachment')}</span>
            {media.length ? <ThumbStrip accountId={accountId} ids={media} byId={cat.byId} max={4} size={30} loading={loading} /> : <span style={{ display: 'flex', alignItems: 'center', gap: 5, font: f(400, 11.5), color: C.muted }}><Icon name="type" size={12} />{t('sch.textOnly')}</span>}
            <Btn kind="dashed" height={26} onClick={() => setPicking(true)}>{media.length ? t('edit.change') : t('edit.attach')}</Btn>
            {media.length > 0 && <Btn kind="text" height={26} onClick={() => setMedia([])}>{t('edit.remove')}</Btn>}
          </div>
        </div>
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 18px', borderTop: `1px solid ${C.divider}` }}>
          <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, font: f(500, 12), color: C.purplePale }}>{bad && <><Icon name="alert-circle" size={13} />{bad}</>}</span>
          <button type="button" onClick={onClose} className="cpt-hover-icon" style={{ height: 30, padding: '0 12px', border: `1px solid ${C.borderHi}`, borderRadius: 7, background: C.input, color: C.text, font: f(500, 12.5), cursor: 'pointer' }}>{t('cancel')}</button>
          <button type="button" onClick={() => { void save(); }} disabled={!!bad || saving} style={{ height: 30, padding: '0 14px', border: 0, borderRadius: 7, background: bad ? C.borderLo : C.purple, color: bad ? C.muted : '#fff', font: f(600, 12.5), cursor: bad || saving ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>{saving && <Spin size={12} />}{t('edit.save')}</button>
        </div>
      </Dialog>
      {picking && (
        <MediaPicker
          accountId={accountId}
          catalogue={cat}
          title={t('edit.pickTitle')}
          initialSelected={media}
          allowEmpty
          fit={(p, v) => (p + v > 1 ? { text: t('edit.oneOnly'), tone: 'warn' } : null)}
          onClose={() => setPicking(false)}
          onConfirm={(picked) => { setMedia(picked.slice(0, 1)); setPicking(false); }}
        />
      )}
    </>
  );
}

function DateTime({ value, onChange }: { value: CalendarDateTime; onChange: (v: CalendarDateTime) => void }) {
  const s: CSSProperties = { height: 28, padding: '0 8px', boxSizing: 'border-box', background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, font: f(500, 12), outline: 'none', colorScheme: 'dark' };
  const hm = `${String(value.hour).padStart(2, '0')}:${String(value.minute).padStart(2, '0')}`;
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input type="date" value={scheduleDayKey(value)} style={{ ...s, width: 140 }} onChange={(e) => {
        const [y, mo, d] = e.target.value.split('-').map(Number);
        if (y && mo && d) onChange(value.set({ year: y, month: mo, day: d }));
      }} />
      <input type="time" value={hm} style={{ ...s, width: 96 }} onChange={(e) => {
        const [h, mi] = e.target.value.split(':').map(Number);
        if (h !== undefined && mi !== undefined && !Number.isNaN(h) && !Number.isNaN(mi)) onChange(value.set({ hour: h, minute: mi, second: 0, millisecond: 0 }));
      }} />
    </div>
  );
}

/** Rebuild a campaign's pending queue from its settings (a server job; polled until done). */
function RegenDialog({ kind, accountId, camps, initial, onClose, onDone }: {
  kind: Kind; accountId: string; camps: Camp[]; initial: number | null; onClose: () => void; onDone: (ok: boolean, text: string) => void;
}) {
  const { t } = useTranslation('massCompact');
  const a = useMemo(() => massApi(kind, accountId), [kind, accountId]);
  const [id, setId] = useState<number | null>(initial);
  const [running, setRunning] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);
  const name = camps.find((c) => c.id === id)?.name ?? '';

  const go = async () => {
    if (id === null || running) return;
    setRunning(true);
    try {
      const req = await a.regenerate(id);
      const start = Date.now();
      timer.current = setInterval(async () => {
        try {
          if (Date.now() - start > 90_000) { clearInterval(timer.current!); onDone(false, t('regen.timeout', { name })); return; }
          const u = await api.getScheduleUpdateRequest(req.id);
          if (u.status === 'satisfied') { clearInterval(timer.current!); onDone(true, t('regen.done', { name })); }
          else if (u.status === 'failed') { clearInterval(timer.current!); onDone(false, u.error_message ? t('regen.failedMsg', { name, error: u.error_message }) : t('regen.failed', { name })); }
        } catch { /* a missed poll is retried on the next tick */ }
      }, 3000);
    } catch (e) {
      console.error('Failed to request schedule regeneration:', e);
      onDone(false, t('regen.requestFailed', { name }));
    }
  };

  return (
    <Dialog width={420} onClose={onClose} locked={running}>
      <DialogHead icon="zap" title={t('regen.title')} onClose={running ? undefined : onClose} closeTitle={t('close')} />
      <div style={{ padding: '14px 18px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', background: 'color-mix(in srgb, var(--om-accent) 12%, transparent)', border: `1px solid ${C.tintLine}`, borderRadius: 7, font: f(500, 12, 1.45), color: C.purplePale }}>
          <Icon name="alert-circle" size={13} style={{ flex: 'none', marginTop: 2 }} /><span>{t('regen.warn')}</span>
        </div>
        <Panel>
          <PanelRow label={t('regen.campaign')}>
            <Menu
              icon="target"
              iconColor={C.purpleLt}
              align="right"
              label={name || t('regen.pick')}
              disabled={running}
              options={camps.map((c) => ({ id: String(c.id), label: c.name, sub: c.active ? '' : t('sch.off'), checked: c.id === id }))}
              onPick={(v) => setId(Number(v))}
            />
          </PanelRow>
        </Panel>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: `1px solid ${C.divider}` }}>
        <button type="button" disabled={running} onClick={onClose} className="cpt-hover-icon" style={{ height: 30, padding: '0 12px', border: `1px solid ${C.borderHi}`, borderRadius: 7, background: C.input, color: C.text, font: f(500, 12.5), cursor: running ? 'default' : 'pointer' }}>{t('cancel')}</button>
        <button type="button" disabled={id === null || running} onClick={() => { void go(); }} style={{ height: 30, padding: '0 14px', border: 0, borderRadius: 7, background: C.purple, color: '#fff', font: f(600, 12.5), cursor: running ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: id === null ? 0.5 : 1 }}>
          {running ? <Spin size={12} /> : <Icon name="zap" size={13} />}{running ? t('regen.running') : t('sch.regenerate')}
        </button>
      </div>
    </Dialog>
  );
}
